/**
 * The card workspace's editor, as a state machine (fix-73, Royce 2026-10-18). The draft is ALWAYS editable in place,
 * with no mode; Save my version, Refine, an answer and Approve each write and then load what the desk holds. Kept out
 * of the component so it can be tested.
 *
 * The poll and every return to the tab fetch the card again, and each fetch is a new object. As on the Knowledge
 * screen (reconcile, 16 Oct), an unsaved edit is never overwritten: a fetch loads only when nothing is typed or the
 * desk already holds what is typed (this editor's own save, read back); otherwise the newer copy is held back and
 * offered. Unsaved text is also kept per card in the browser's session (draftStore), so leaving the screen and coming
 * back keeps it.
 */

export interface Fields { subject: string; body: string }
export type Phase = 'idle' | 'saving' | 'refining' | 'answering' | 'sending';

export interface EditorState {
  cardId: number | null;
  /** What the desk held when the editor last loaded; dirtiness is measured against it. */
  base: Fields | null;
  /** What the editor holds. */
  draft: Fields;
  /** A newer saved copy, held back because the editor has unsaved edits. */
  newer: Fields | null;
  phase: Phase;
  error: string | null;
}

export type EditorEvent =
  | { type: 'fetched'; cardId: number; fields: Fields }
  | { type: 'typed'; fields: Partial<Fields> }
  | { type: 'start'; phase: Exclude<Phase, 'idle'> }
  | { type: 'finished'; fields?: Fields }
  | { type: 'failed'; error: string }
  | { type: 'discard' }
  | { type: 'restore'; base: Fields; draft: Fields };

export const EMPTY_FIELDS: Fields = { subject: '', body: '' };
export const initialEditor: EditorState = { cardId: null, base: null, draft: EMPTY_FIELDS, newer: null, phase: 'idle', error: null };

export const same = (a: Fields | null, b: Fields | null) => !!a && !!b && a.subject === b.subject && a.body === b.body;
export const dirty = (s: EditorState) => !!s.base && !same(s.base, s.draft);

const load = (s: EditorState, cardId: number, fields: Fields): EditorState =>
  ({ ...s, cardId, base: fields, draft: fields, newer: null, phase: 'idle', error: null });

export function editorReducer(s: EditorState, e: EditorEvent): EditorState {
  switch (e.type) {
    case 'fetched':
      if (s.cardId !== e.cardId || !s.base) return load(s, e.cardId, e.fields);
      if (same(e.fields, s.base)) return s;                // the same saved copy
      if (s.phase !== 'idle') return s;                     // a write is in flight; its answer decides
      if (!dirty(s) || same(e.fields, s.draft)) return load(s, e.cardId, e.fields);
      return same(e.fields, s.newer) ? s : { ...s, newer: e.fields };
    case 'typed':
      if (s.phase !== 'idle') return s;
      return { ...s, draft: { ...s.draft, ...e.fields }, error: null };
    case 'start':
      return s.phase === 'idle' ? { ...s, phase: e.phase, error: null } : s;
    case 'finished':
      return e.fields && s.cardId !== null ? load(s, s.cardId, e.fields) : { ...s, phase: 'idle' };
    case 'failed':
      return { ...s, phase: 'idle', error: e.error };
    case 'discard':
      return s.newer && s.cardId !== null ? load(s, s.cardId, s.newer) : s;
    case 'restore':
      if (!s.base || same(e.draft, s.base)) return s;
      // Restored on top of a copy that has moved since: keep the text and offer the desk's copy.
      return { ...s, draft: e.draft, newer: same(e.base, s.base) ? null : s.base };
    default:
      return s;
  }
}

// ---- Unsaved text across leaving the screen ----

interface StoreLike { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void }
const key = (cardId: number) => `card-draft:${cardId}`;

/** Keep or clear a card's unsaved text. Never throws: storage can be missing or refused. */
export function keepDraft(store: StoreLike | null, s: EditorState): void {
  if (!store || s.cardId === null) return;
  try {
    if (dirty(s)) store.setItem(key(s.cardId), JSON.stringify({ base: s.base, draft: s.draft }));
    else store.removeItem(key(s.cardId));
  } catch { /* storage refused: the edit lives only in this screen */ }
}

export function keptDraft(store: StoreLike | null, cardId: number): { base: Fields; draft: Fields } | null {
  if (!store) return null;
  try {
    const v = JSON.parse(store.getItem(key(cardId)) ?? 'null');
    return v && typeof v.draft?.body === 'string' && typeof v.base?.body === 'string' ? v : null;
  } catch { return null; }
}

// ---- Keys ----

export type KeyAction = 'save' | 'approve' | 'dismiss' | 'reject' | 'leave'
  | 'next' | 'prev' | 'queue' | 'focus' | 'close' | null;

/**
 * S save · A approve · D dismiss · R reject, outside the editor (a letter typed into the draft is a letter);
 * ⌘↩ approves and sends from anywhere; ⌘S saves and Esc leaves the field, from inside it. E means nothing.
 * fix-74, the room around the card, also outside a field: J next card · K previous · Q the queue · F focus ·
 * Esc closes the context slide-over.
 */
export function keyAction(k: { key: string; meta: boolean; ctrl: boolean; alt: boolean; inField: boolean }): KeyAction {
  const mod = k.meta || k.ctrl;
  if (mod && k.key === 'Enter') return 'approve';
  if (mod && (k.key === 's' || k.key === 'S') && !k.alt) return 'save';
  if (k.key === 'Escape') return k.inField ? 'leave' : 'close';
  if (k.inField || mod || k.alt) return null;
  return ({
    s: 'save', a: 'approve', d: 'dismiss', r: 'reject', j: 'next', k: 'prev', q: 'queue', f: 'focus',
  } as Record<string, KeyAction>)[k.key.toLowerCase()] ?? null;
}

// ---- What the editor says about the text ----

/** The rules line: pass, or the rule the text in the editor would fail. `checked` is the text the check was run on. */
export function rulesLine(check: { refusal: string | null; would_fail: string[]; flags: string[] } | null,
  checked: string | null, text: string): { tone: 'ok' | 'warn' | 'bad' | 'wait'; text: string } {
  if (!check || checked !== text) return { tone: 'wait', text: 'checking the rules…' };
  if (check.refusal) return { tone: 'bad', text: `would not send: ${check.refusal.replace(/^the reply /, '')}` };
  if (check.would_fail.length) {
    return { tone: 'warn', text: `your text is yours to send, but a drafted reply would be refused: it ${check.would_fail.join('; ')}` };
  }
  if (check.flags.length) return { tone: 'warn', text: `passes all rules · check: it ${check.flags.join('; ')}` };
  return { tone: 'ok', text: 'passes all rules' };
}

/** "AI draft → refined by you 14:02 → edited by you 14:10", with "· unsaved edits" while the editor has them. */
export function versionLine(versions: { v: number; kind: string; at: string }[] | undefined, time: (at: string) => string,
  unsaved: boolean): string {
  const vs = versions?.length ? versions : [{ v: 1, kind: 'AI draft', at: '' }];
  const line = vs.map((v, i) => (i === 0 ? v.kind : `${v.kind} ${time(v.at)}`)).join(' → ');
  return unsaved ? `${line} · unsaved edits` : line;
}
